import { Suspense, useEffect } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { Outlet, RouteObject } from 'react-router-dom'
import { AuthenticatedChatbot } from '~components/chatbot'
import { GuidedTour } from '~components/guided-tour/guided-tour'
import LoadComponent from '~components/molecules/load-component/load-component'
import { NavigationProvider } from '~components/navigation-and-footer/navigation/navigation-context'
import { PageGuard } from '~components/page-guard'
import { TermsOfUseModal } from '~components/terms-of-use-modal'
import { WelcomePopup } from '~components/welcome-popup/welcome-popup'
import { ActivePoolProvider } from '~context/active-pool-context'
import { Auth0ProviderWithConfig } from '../components/auth0-provider-with-config'
import { AuthenticationGuard } from '../components/authentication-guard'
import NavigationAndFooter from '../components/navigation-and-footer/navigation-and-footer'
import ErrorPage from '../pages/error-page'
import { openPages } from './open-pages'
import {
  PageAccess,
  routesBehindLogin,
} from './routes-behind-login/routes-behind-login'

interface RouteHandle {
  restrictedTo?: (string | RegExp)[]
  withPools?: boolean
  disableLeftNavigation?: boolean
  disallowedScopes?: string[]
  pageAccess: PageAccess
}

export type ExtendedRouteObject = RouteObject & {
  handle: RouteHandle
}

export const pagesBehindLogin = [
  {
    element: (
      <AuthenticationGuard>
        <PageGuard>
          <Outlet />
        </PageGuard>
      </AuthenticationGuard>
    ),
    children: [...routesBehindLogin],
  },
]

const FaviconWrapper = ({ children }: { children: React.ReactNode }) => {
  const host = window.location.host

  useEffect(() => {
    const setFavicon = () => {
      const favicon = document.getElementById('favicon') as HTMLAnchorElement

      if (host.includes('localhost') || host.includes('test')) {
        favicon.href = '/favicon-test.svg'
      }
      if (host.includes('stage')) {
        favicon.href = '/favicon-stage.svg'
      }
    }
    setFavicon()
  }, [host])

  return <>{children}</>
}

export const routeWrapper: RouteObject[] = [
  {
    element: (
      <FaviconWrapper>
        <Auth0ProviderWithConfig>
          <HelmetProvider>
            <ActivePoolProvider>
              <TermsOfUseModal />
              <WelcomePopup />
              <AuthenticatedChatbot />
              <NavigationProvider>
                <GuidedTour />
                <NavigationAndFooter>
                  <Suspense fallback={<LoadComponent />}>
                    <Outlet />
                  </Suspense>
                </NavigationAndFooter>
              </NavigationProvider>
            </ActivePoolProvider>
          </HelmetProvider>
        </Auth0ProviderWithConfig>
      </FaviconWrapper>
    ),
    errorElement: <ErrorPage />,
    children: [...openPages, ...pagesBehindLogin],
  },
]
